import { JaneGoldLearnMoreLink } from '@iheartjane/menu';
import {
  Box,
  Button,
  Flex,
  JaneGoldIcon,
  JaneGoldLogo,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';

import { useJaneGoldData } from '@jane/shared-ecomm/hooks';

import { BankAccountDetails } from '../../bankAccountDetails/bankAccountDetails';
import type { JaneGoldCartCalloutProps } from '../janeGoldCallout.types';
import { JaneGoldFeeBreakdown } from '../janeGoldFeeBreakdown/janeGoldFeeBreakdown';
import { JaneGoldTermsLink } from '../janeGoldTermsLink/janeGoldTermsLink';

/**
 * Checkout variant of the Jane Gold callout action component. If the user doesn't have a linked bank, it will display a Jane Gold Logo `Learn more` link and `Link bank account` button. If the user has a linked bank, it will list the linked bank information and hide the `Link bank account` button.
 */
export const JaneGoldCheckoutCallout = ({
  cartBrandDiscounts,
  buttonLabel,
  description,
  discountLabel,
  feeLabel,
  hasBreakdown,
  onLinkAccountClick,
  title,
  totalLabel,
}: JaneGoldCartCalloutProps) => {
  const isMobile = useMobileMediaQuery({});
  const { selectedBankAccount, userHasLinkedBank } = useJaneGoldData();

  return (
    <>
      {hasBreakdown && userHasLinkedBank ? (
        <>
          <Flex alignItems="center" justifyContent="start" pb={20}>
            {!isMobile && <JaneGoldIcon mr={8} />}
            <Typography as="h2" branded variant="header-bold">
              {title}
            </Typography>
          </Flex>

          <JaneGoldFeeBreakdown
            buttonLabel={buttonLabel}
            cartBrandDiscounts={cartBrandDiscounts}
            description={description}
            discountLabel={discountLabel}
            feeLabel={feeLabel}
            onLinkAccountClick={onLinkAccountClick}
            totalLabel={totalLabel}
            variant="checkout"
          />
        </>
      ) : (
        <>
          <Box>
            <Typography
              mb={isMobile ? 20 : 24}
              as="h2"
              branded
              variant="header-bold"
            >
              {title}
            </Typography>
          </Box>
          <Flex flexDirection="column">
            {!userHasLinkedBank && (
              <JaneGoldLogo altText="Jane Gold logo" mr="auto" />
            )}
            <Flex
              gap={isMobile ? 24 : 64}
              flexDirection={isMobile ? 'column' : 'row'}
            >
              <Typography as="span" variant="body">
                {description}
                &nbsp;
                {!userHasLinkedBank ? (
                  <JaneGoldTermsLink label="Learn more" />
                ) : (
                  <JaneGoldLearnMoreLink userHasLinkedBank />
                )}
              </Typography>
              {!userHasLinkedBank && (
                <Button
                  full={isMobile}
                  ml="auto"
                  onClick={onLinkAccountClick}
                  variant="secondary"
                  label="Link bank account"
                />
              )}
            </Flex>
            {userHasLinkedBank && selectedBankAccount && (
              <BankAccountDetails
                bankAccount={selectedBankAccount}
                key={selectedBankAccount.bank_account_id}
                hideJanePayBranding
                hidePoweredByJane
                mt={24}
              />
            )}
          </Flex>
        </>
      )}
    </>
  );
};
